import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { Player } from "../interfaces/Team";
import HighlightsDialog from "./HighlightsDialog";

interface PlayerSelectProps {
  players: Player[];
  value: number | string | undefined;
  onChange?: (event: SelectChangeEvent<string>) => void;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  inputHeight: number;
  disabledOptions?: number[];
  isAdmin: boolean;
  // Highlight functionality props
  matchId?: number;
  matchType?: "singles" | "doubles";

  direction: "ltr" | "rtl";
}

const PlayerSelect: React.FC<PlayerSelectProps> = ({
  players,
  value,
  onChange,
  placeholder = "Spieler wählen",
  name,
  inputHeight,
  disabledOptions,
  isAdmin,
  // Highlight props
  matchId,
  matchType = "singles",

  direction = "ltr",
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [highlightDialogOpen, setHighlightDialogOpen] = React.useState<
    number | null
  >(null);
  const [selectedPlayer, setSelectedPlayer] = React.useState<number | "">("");

  // Helper function to format player name based on screen size
  const formatPlayerName = (playerName: string) => {
    if (isMobile || !isAdmin) {
      return playerName.split(" ")[0]; // Show only first name on mobile
    }
    return playerName; // Show full name on desktop
  };

  const handleHighlightClick = () => {
    console.log(" i am here ", value, matchId);
    if (matchId && value) {
      setHighlightDialogOpen(matchId);
      setSelectedPlayer(value as number);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        direction: direction,
        width: "100%",
      }}
    >
      {isAdmin && (
        <IconButton
          size="small"
          color="primary"
          onClick={handleHighlightClick}
          aria-label="Highlight"
        >
          <EmojiEventsIcon fontSize="small" />
        </IconButton>
      )}
      <FormControl fullWidth size="small">
        <Select
          disabled={!isAdmin}
          value={value?.toString() ?? ""}
          onChange={onChange}
          name={name}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#999" }}>
                  {isMobile ? "Spieler" : placeholder}
                </span>
              );
            }
            const selectedPlayer = players.find(
              (p) => p.id.toString() === selected
            );
            if (selectedPlayer) {
              let displayName = formatPlayerName(selectedPlayer.name);
              if ((selectedPlayer as any).displayName) {
                displayName = formatPlayerName(
                  (selectedPlayer as any).displayName
                );
              }
              return displayName;
            }
            return selected;
          }}
          sx={{
            height: inputHeight,
            minWidth: 0,
            fontSize: isMobile ? 12 : 16,
            borderRadius: 0,
            border: "1px solid #bbb",
            background: "#fff",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiSelect-select": {
              padding: isMobile ? "4px 6px" : "8px 12px",
              paddingRight: isMobile ? "6px !important" : "24px !important",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& .MuiSelect-icon": {
              right: "2px",
              display: isMobile ? "none" : "block",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #1976d2",
            },
          }}
        >
          <MenuItem value="" disabled sx={{ display: "none" }}>
            {isMobile ? "Spieler" : placeholder}
          </MenuItem>
          {players.map((p) => {
            const isSelected = String(p.id) === String(value);
            let label = formatPlayerName(p.name);
            if (isSelected && (p as any).displayName)
              label = formatPlayerName((p as any).displayName);
            else if ((p as any).displayName)
              label = formatPlayerName((p as any).displayName);
            // Disable if in disabledOptions and not the current value
            const isDisabled =
              (!!disabledOptions?.includes(p.id) && !isSelected) || !isAdmin;
            return (
              <MenuItem
                key={p.id}
                value={p.id.toString()}
                disabled={isDisabled}
                sx={{
                  fontSize: isMobile ? 12 : 16,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minHeight: "auto",
                  paddingY: 0.5,
                }}
              >
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* HighlightsDialog */}
      {matchId &&
        highlightDialogOpen === matchId &&
        setHighlightDialogOpen &&
        setSelectedPlayer &&
        (() => {
          // Get the selected player's name
          const selectedPlayerData = players.find(
            (p) => p.id.toString() === value?.toString()
          );
          let playerName = "";
          if (selectedPlayerData) {
            playerName =
              (selectedPlayerData as any).displayName ||
              selectedPlayerData.name;
          }

          return (
            <HighlightsDialog
              open={true}
              onClose={() => setHighlightDialogOpen(null)}
              playermatchId={matchId}
              selectedPlayer={selectedPlayer}
              setSelectedPlayer={setSelectedPlayer}
              matchType={matchType}
              PlayerName={playerName}
            />
          );
        })()}
    </Box>
  );
};

export default PlayerSelect;
